import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react"
import type { Account, Listing } from "@/types"
import type { TextPosition, TextAlign } from "./constants"
import { FONT_OPTIONS, FONT_SIZE_OPTIONS, FONT_WEIGHT_OPTIONS, RENDER_DIMS } from "./constants"

// ---------------------------------------------------------------------------
// Font / size helpers (mirror remotion/src/lib/fonts.ts)
// ---------------------------------------------------------------------------

const FONT_DISPLAY = "'Playfair Display Variable', serif"
const FONT_BODY = "'DM Sans Variable', sans-serif"

function resolveFont(fontId: string | undefined, fallback: string): string {
  if (!fontId) return fallback
  const match = FONT_OPTIONS.find((f) => f.id === fontId)
  return match ? `'${match.family}', sans-serif` : fallback
}

function resolveFontSize(sizeId: string | undefined, fallback: number): number {
  if (!sizeId) return fallback
  const match = FONT_SIZE_OPTIONS.find((s) => s.id === sizeId)
  return match ? match.remotionPx : fallback
}

function resolveFontWeight(weightId: string | undefined, fallback: number): number {
  if (!weightId) return fallback
  const match = FONT_WEIGHT_OPTIONS.find((w) => w.id === weightId)
  return match ? match.value : fallback
}

// ---------------------------------------------------------------------------
// Template styles (mirror remotion/src/lib/templates.ts exactly)
// ---------------------------------------------------------------------------

type CompStyle = {
  sceneText: CSSProperties
  headline: CSSProperties
  scrimGradient: string
}

const COMP_STYLES: Record<string, CompStyle> = {
  classic: {
    sceneText: { fontFamily: FONT_DISPLAY, fontSize: 56, lineHeight: 1.3, color: "white", fontWeight: 400 },
    headline: { fontFamily: FONT_DISPLAY, fontSize: 76, fontWeight: 700, color: "white", lineHeight: 1.2 },
    scrimGradient: "linear-gradient(to top, rgba(26,26,26,0.7) 0%, transparent 100%)",
  },
  modern: {
    sceneText: { fontFamily: FONT_BODY, fontSize: 52, lineHeight: 1.3, color: "white", fontWeight: 700, textTransform: "uppercase", letterSpacing: 3 },
    headline: { fontFamily: FONT_BODY, fontSize: 72, fontWeight: 700, textTransform: "uppercase", letterSpacing: 4, color: "white", lineHeight: 1.2 },
    scrimGradient: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
  },
  luxury: {
    sceneText: { fontFamily: FONT_DISPLAY, fontSize: 54, lineHeight: 1.4, color: "white", fontWeight: 400, fontStyle: "italic" },
    headline: { fontFamily: FONT_DISPLAY, fontSize: 74, fontWeight: 600, fontStyle: "italic", color: "white", lineHeight: 1.2 },
    scrimGradient: "linear-gradient(to top, rgba(26,26,26,0.6) 0%, transparent 100%)",
  },
  bold: {
    sceneText: { fontFamily: FONT_BODY, fontSize: 58, lineHeight: 1.2, color: "white", fontWeight: 900, textTransform: "uppercase" },
    headline: { fontFamily: FONT_BODY, fontSize: 80, fontWeight: 900, textTransform: "uppercase", color: "white", lineHeight: 1.2 },
    scrimGradient: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
  },
}

// ---------------------------------------------------------------------------
// ScaledFrame — renders children at Remotion resolution, CSS-scaled to fit
// ---------------------------------------------------------------------------

export function ScaledFrame({
  aspectRatio,
  children,
}: {
  aspectRatio: string
  children: ReactNode
}) {
  const dims = RENDER_DIMS[aspectRatio as keyof typeof RENDER_DIMS] || RENDER_DIMS.landscape
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const w = el.clientWidth
      if (w > 0) setScale(w / dims.w)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [dims.w])

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        aspectRatio: `${dims.w} / ${dims.h}`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          width: dims.w,
          height: dims.h,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          visibility: scale > 0 ? "visible" : "hidden",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AgentBrandingBar — mirrors AgentBranding.tsx
// ---------------------------------------------------------------------------

function AgentBrandingBar({
  agencyName,
  logoUrl,
  brandColour,
  websiteUrl,
  ctaText,
  ctaButtonTextColour,
  showButton = true,
  isPortrait,
}: {
  agencyName: string
  logoUrl?: string
  brandColour: string
  websiteUrl?: string
  ctaText: string
  ctaButtonTextColour?: string
  showButton?: boolean
  isPortrait: boolean
}) {
  const barHeight = isPortrait ? 200 : 150

  return (
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 10 }}>
      {/* Branding bar */}
      <div
        style={{
          height: barHeight,
          background:
            "linear-gradient(to bottom, rgba(26, 26, 26, 0.6) 0%, rgba(26, 26, 26, 0.9) 100%)",
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          gap: 28,
          padding: "0 48px",
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt=""
            style={{ height: barHeight * 0.45, objectFit: "contain", flexShrink: 0 }}
          />
        )}

        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
          <span
            style={{
              fontFamily: FONT_BODY,
              fontSize: isPortrait ? 36 : 30,
              fontWeight: 500,
              color: "rgba(255,255,255,0.9)",
              letterSpacing: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {agencyName}
          </span>
          {websiteUrl && (
            <span
              style={{
                fontFamily: FONT_BODY,
                fontSize: isPortrait ? 26 : 22,
                fontWeight: 400,
                color: "rgba(255,255,255,0.55)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {websiteUrl}
            </span>
          )}
        </div>

        {showButton && ctaText && (
          <span
            style={{
              fontFamily: FONT_BODY,
              fontSize: isPortrait ? 38 : 32,
              fontWeight: 600,
              color: ctaButtonTextColour || "white",
              backgroundColor: brandColour,
              padding: "16px 44px",
              borderRadius: 10,
              flexShrink: 0,
            }}
          >
            {ctaText}
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SceneComposition — mirrors SceneSlideshow.tsx + AgentBranding.tsx
// ---------------------------------------------------------------------------

export function SceneComposition({
  text,
  mediaUrl,
  mediaType,
  templateVariant,
  textPosition = "bottom",
  textAlign = "center",
  textFont,
  textSize,
  textColour,
  textUppercase,
  textWeight,
  showCta = true,
  showCtaButton = true,
  ctaButtonTextColour,
  account,
  ctaText,
  aspectRatio,
}: {
  text: string
  mediaUrl?: string
  mediaType?: "image" | "video"
  templateVariant: string
  textPosition?: TextPosition
  textAlign?: TextAlign
  textFont?: string
  textSize?: string
  textColour?: string
  textUppercase?: boolean
  textWeight?: string
  showCta?: boolean
  showCtaButton?: boolean
  ctaButtonTextColour?: string
  account: Account
  ctaText: string
  aspectRatio: string
}) {
  const style = COMP_STYLES[templateVariant] || COMP_STYLES.classic
  const dims = RENDER_DIMS[aspectRatio as keyof typeof RENDER_DIMS] || RENDER_DIMS.landscape
  const isPortrait = dims.h > dims.w

  const barTotal = showCta ? (isPortrait ? 200 : 150) : 0
  const brandColour = account.brand_colour || "#C41E2A"

  return (
    <div style={{ width: dims.w, height: dims.h, backgroundColor: "#1A1A1A", position: "relative", overflow: "hidden" }}>
      {/* Media layer */}
      {mediaUrl && mediaType === "video" ? (
        <video
          src={mediaUrl}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          autoPlay
          loop
          muted
          playsInline
        />
      ) : mediaUrl ? (
        <img src={mediaUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : null}

      {/* Scrim + text */}
      {text && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            ...(textPosition === "top"
              ? { top: 0, padding: "48px 48px 80px", background: style.scrimGradient.replace("to top", "to bottom") }
              : textPosition === "middle"
                ? { top: "50%", transform: "translateY(-50%)", padding: "48px", background: "rgba(26,26,26,0.3)" }
                : { bottom: 0, padding: `80px 48px ${72 + barTotal}px`, background: style.scrimGradient }),
          }}
        >
          <p
            style={{
              ...style.sceneText,
              ...(textFont ? { fontFamily: resolveFont(textFont, style.sceneText.fontFamily as string) } : {}),
              ...(textSize ? { fontSize: resolveFontSize(textSize, style.sceneText.fontSize as number) } : {}),
              ...(textColour ? { color: textColour } : {}),
              ...(textWeight ? { fontWeight: resolveFontWeight(textWeight, style.sceneText.fontWeight as number) } : {}),
              ...(textUppercase !== undefined
                ? { textTransform: textUppercase ? ("uppercase" as const) : ("none" as const) }
                : {}),
              margin: 0,
              textShadow: "0 2px 12px rgba(0,0,0,0.5)",
              textAlign,
            }}
          >
            {text}
          </p>
        </div>
      )}

      {/* CTA bar */}
      {showCta && (
        <AgentBrandingBar
          agencyName={account.agency_name || account.name}
          logoUrl={account.logo_url}
          brandColour={brandColour}
          websiteUrl={account.website_url}
          ctaText={ctaText}
          ctaButtonTextColour={ctaButtonTextColour}
          showButton={showCtaButton}
          isPortrait={isPortrait}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TitleComposition — mirrors TitleCard.tsx (logo + colour/gradient)
// ---------------------------------------------------------------------------

function computeTitleBackground(
  bgColour: string,
  bgGradient?: "none" | "linear-top" | "linear-bottom" | "radial",
  bgGradientEnd?: string,
): string {
  if (!bgGradient || bgGradient === "none") return bgColour
  const endColour = bgGradientEnd || "#1A1A1A"
  switch (bgGradient) {
    case "linear-top":
      return `linear-gradient(to bottom, ${bgColour}, ${endColour})`
    case "linear-bottom":
      return `linear-gradient(to top, ${bgColour}, ${endColour})`
    case "radial":
      return `radial-gradient(circle at center, ${bgColour}, ${endColour})`
    default:
      return bgColour
  }
}

export function TitleComposition({
  headline,
  logoUrl,
  bgColour,
  bgGradient,
  bgGradientEnd,
  logoPosition = "above",
  logoSize = "small",
  templateVariant,
  textPosition = "bottom",
  textAlign = "center",
  textFont,
  textSize,
  textColour,
  textUppercase,
  textWeight,
  aspectRatio,
}: {
  headline?: string
  logoUrl?: string
  bgColour?: string
  bgGradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  bgGradientEnd?: string
  logoPosition?: "above" | "below" | "hidden"
  logoSize?: "small" | "medium" | "large"
  templateVariant: string
  textPosition?: TextPosition
  textAlign?: TextAlign
  textFont?: string
  textSize?: string
  textColour?: string
  textUppercase?: boolean
  textWeight?: string
  aspectRatio: string
}) {
  const compStyle = COMP_STYLES[templateVariant] || COMP_STYLES.classic
  const dims = RENDER_DIMS[aspectRatio as keyof typeof RENDER_DIMS] || RENDER_DIMS.landscape
  const isPortrait = dims.h > dims.w

  const solidColour = bgColour || "#1A1A1A"
  const background = computeTitleBackground(solidColour, bgGradient, bgGradientEnd)

  const logoScale = logoSize === "large" ? 2 : logoSize === "medium" ? 1.5 : 1
  const maxLogoHeight = (isPortrait ? 180 : 120) * logoScale
  const maxLogoWidth = (isPortrait ? 500 : 400) * logoScale
  const showLogo = logoPosition !== "hidden" && !!logoUrl

  const logoElement = showLogo ? (
    <img
      src={logoUrl}
      alt=""
      style={{
        maxHeight: maxLogoHeight,
        maxWidth: maxLogoWidth,
        objectFit: "contain",
      }}
    />
  ) : null

  const headlineElement = headline ? (
    <p
      style={{
        ...compStyle.headline,
        ...(textFont ? { fontFamily: resolveFont(textFont, compStyle.headline.fontFamily as string) } : {}),
        ...(textSize ? { fontSize: resolveFontSize(textSize, compStyle.headline.fontSize as number) } : {}),
        ...(textColour ? { color: textColour } : {}),
        ...(textWeight ? { fontWeight: resolveFontWeight(textWeight, compStyle.headline.fontWeight as number) } : {}),
        ...(textUppercase !== undefined
          ? { textTransform: textUppercase ? ("uppercase" as const) : ("none" as const) }
          : {}),
        margin: 0,
        lineHeight: 1.2,
        textAlign,
        whiteSpace: "pre-line",
      }}
    >
      {headline}
    </p>
  ) : null

  const justifyContent = textPosition === "top" ? "flex-start" : textPosition === "bottom" ? "flex-end" : "center"
  const paddingY = textPosition === "top"
    ? { paddingTop: isPortrait ? 200 : 120 }
    : textPosition === "bottom"
      ? { paddingBottom: isPortrait ? 200 : 120 }
      : {}

  return (
    <div
      style={{
        width: dims.w,
        height: dims.h,
        background,
        display: "flex",
        flexDirection: "column",
        alignItems: textAlign === "left" ? "flex-start" : textAlign === "right" ? "flex-end" : "center",
        justifyContent,
        padding: isPortrait ? "0 72px" : "0 96px",
        gap: isPortrait ? 48 : 36,
        overflow: "hidden",
        ...paddingY,
      }}
    >
      {logoPosition === "above" && logoElement}
      {headlineElement}
      {logoPosition === "below" && logoElement}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ClosingComposition — mirrors ClosingScene.tsx
// ---------------------------------------------------------------------------

export function ClosingComposition({
  listing,
  account,
  closingBgColour,
  closingBgGradient,
  closingBgGradientEnd,
  closingFont,
  closingSize,
  closingColour,
  closingUppercase,
  closingWeight,
  closingLogoSize = "small",
  showWatermark = true,
  aspectRatio,
}: {
  listing: Listing
  account: Account
  closingBgColour?: string
  closingBgGradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  closingBgGradientEnd?: string
  closingFont?: string
  closingSize?: string
  closingColour?: string
  closingUppercase?: boolean
  closingWeight?: string
  closingLogoSize?: "small" | "medium" | "large"
  showWatermark?: boolean
  aspectRatio?: string
}) {
  const dims = RENDER_DIMS[aspectRatio as keyof typeof RENDER_DIMS] || RENDER_DIMS.landscape
  const isPortrait = dims.h > dims.w
  const logoScale = closingLogoSize === "large" ? 2 : closingLogoSize === "medium" ? 1.5 : 1
  const maxLogoHeight = (isPortrait ? 180 : 120) * logoScale
  const maxLogoWidth = (isPortrait ? 500 : 400) * logoScale
  const brandColour = account.brand_colour || "#C41E2A"
  const textColour = closingColour || "white"
  const solidColour = closingBgColour || brandColour
  const closingBackground = (() => {
    if (!closingBgGradient || closingBgGradient === "none") return solidColour
    const endColour = closingBgGradientEnd || "#1A1A1A"
    switch (closingBgGradient) {
      case "linear-top":
        return `linear-gradient(to bottom, ${solidColour}, ${endColour})`
      case "linear-bottom":
        return `linear-gradient(to top, ${solidColour}, ${endColour})`
      case "radial":
        return `radial-gradient(circle at center, ${solidColour}, ${endColour})`
      default:
        return solidColour
    }
  })()

  const details = [
    listing.bedrooms ? `${listing.bedrooms} bed` : null,
    listing.bathrooms ? `${listing.bathrooms} bath` : null,
  ].filter(Boolean)

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Main content */}
      <div
        style={{
          flex: 1,
          background: closingBackground,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 48,
          textAlign: "center",
        }}
      >
        {account.logo_url && (
          <img
            src={account.logo_url}
            alt=""
            style={{ maxHeight: maxLogoHeight, maxWidth: maxLogoWidth, objectFit: "contain", marginBottom: 36 }}
          />
        )}

        <p
          style={{
            fontFamily: resolveFont(closingFont, FONT_DISPLAY),
            fontSize: resolveFontSize(closingSize, 64),
            fontWeight: resolveFontWeight(closingWeight, 600),
            color: textColour,
            lineHeight: 1.2,
            margin: 0,
            ...(closingUppercase !== undefined
              ? { textTransform: closingUppercase ? ("uppercase" as const) : ("none" as const) }
              : {}),
          }}
        >
          {listing.address}
        </p>

        {listing.price && (
          <p
            style={{
              fontFamily: resolveFont(closingFont, FONT_BODY),
              fontSize: 48,
              fontWeight: 500,
              color: textColour === "white" ? "rgba(255,255,255,0.9)" : `${textColour}E6`,
              margin: "12px 0 0",
            }}
          >
            {listing.price}
          </p>
        )}

        {details.length > 0 && (
          <p
            style={{
              fontFamily: resolveFont(closingFont, FONT_BODY),
              fontSize: 44,
              fontWeight: 500,
              color: textColour === "white" ? "rgba(255,255,255,0.75)" : `${textColour}BF`,
              margin: "12px 0 0",
            }}
          >
            {details.join(" \u00b7 ")}
          </p>
        )}

        {/* Divider */}
        <div
          style={{
            width: 64,
            height: 2,
            backgroundColor: textColour === "white" ? "rgba(255,255,255,0.3)" : `${textColour}4D`,
            margin: "32px 0",
          }}
        />

        <p
          style={{
            fontFamily: FONT_BODY,
            fontSize: 38,
            fontWeight: 500,
            color: textColour === "white" ? "rgba(255,255,255,0.9)" : `${textColour}E6`,
            margin: 0,
          }}
        >
          {account.agency_name || account.name}
        </p>

        {account.website_url && (
          <p
            style={{
              fontFamily: FONT_BODY,
              fontSize: 30,
              fontWeight: 400,
              color: textColour === "white" ? "rgba(255,255,255,0.6)" : `${textColour}99`,
              margin: "8px 0 0",
            }}
          >
            {account.website_url}
          </p>
        )}
      </div>

      {/* QuiteRight watermark strip */}
      {showWatermark && (
      <div
        style={{
          height: 340,
          backgroundColor: "#1A1A1A",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {/* Accent line */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "20%",
            right: "20%",
            height: 3,
            backgroundColor: "#C41E2A",
            opacity: 0.4,
          }}
        />
        <p
          style={{
            fontFamily: FONT_BODY,
            fontSize: 22,
            fontWeight: 400,
            color: "rgba(255,255,255,0.45)",
            letterSpacing: 2,
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          Made with
        </p>
        <img
          src="/qr-logo-wordmark-red.png"
          alt="QuiteRight"
          style={{ height: 120, objectFit: "contain" }}
        />
      </div>
      )}
    </div>
  )
}
